import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileStack, Plus, Repeat, Search, Truck } from "lucide-react";
import {
  formatMoney,
  SALES_DOCUMENT_STATUS_LABELS,
  SALES_DOCUMENT_STATUS_TONE,
  SALES_DOCUMENT_TYPE_LABELS,
} from "@finora/config";
import { SalesDocumentStatus, SalesDocumentType } from "@finora/types";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";

type TabKey = "QUOTE" | "INVOICE" | "DELIVERY_NOTE";

export function SalesDocumentsPage() {
  const [tab, setTab] = useState<TabKey>("QUOTE");
  // Otpremnice tab se prikazuje samo ako firma ima aktivno "Magacinsko
  // poslovanje" (modul "magacin") - vidi razgovor "Otpremnice ima u
  // prikazu samo u slucaju da postoji Magacinsko poslovanje".
  const { data: entitlements } = useQuery({
    queryKey: ["entitlements"],
    queryFn: () => api.getEntitlements(),
    staleTime: 60_000,
  });
  const hasWarehouseModule = entitlements ? entitlements.modules.includes("magacin") : true;

  useEffect(() => {
    if (tab === "DELIVERY_NOTE" && entitlements && !hasWarehouseModule) {
      setTab("QUOTE");
    }
  }, [tab, entitlements, hasWarehouseModule]);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<SalesDocumentStatus | "">("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const navigate = useNavigate();

  // "Ajax" pretraga - filtrira dok kucaš, sa malim kašnjenjem da ne gađamo
  // API na svaki taster.
  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ["sales-documents", tab, search, status, dateFrom, dateTo],
    queryFn: () =>
      api.listSalesDocuments({
        type: tab as SalesDocumentType,
        status: status || undefined,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
    enabled: tab !== "DELIVERY_NOTE",
    staleTime: 0,
  });

  const { data: deliveryNotes, isLoading: isLoadingDeliveryNotes } = useQuery({
    queryKey: ["delivery-notes"],
    queryFn: () => api.listDeliveryNotes(),
    enabled: tab === "DELIVERY_NOTE",
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Prodaja</h1>
          <p className="text-sm text-slate-500">Ponude i fakture - centralni prodajni dokumenti</p>
        </div>
        <div className="flex items-center gap-3">
          {tab === "INVOICE" && (
            <button
              onClick={() => navigate("/ponavljajuce-fakture")}
              className="flex items-center gap-1.5 text-sm text-brand hover:underline"
            >
              <Repeat className="h-3.5 w-3.5" aria-hidden />
              Ponavljajuće fakture
            </button>
          )}
          <Button
            onClick={() => navigate(tab === "QUOTE" ? "/prodaja/nova-ponuda" : "/prodaja/nova-faktura")}
            className={tab === "DELIVERY_NOTE" ? "invisible" : undefined}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {tab === "QUOTE" ? "Nova ponuda" : "Nova faktura"}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {([
          { key: "QUOTE", label: "Ponude" },
          { key: "INVOICE", label: "Fakture" },
          ...(hasWarehouseModule ? [{ key: "DELIVERY_NOTE", label: "Otpremnice" }] : []),
        ] as { key: TabKey; label: string }[]).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== "DELIVERY_NOTE" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Pretraga (broj ili kupac)
          </label>
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Ukucaj broj dokumenta, ime kupca ili PIB..."
              className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
            />
          </label>
        </div>

        <div className="w-44">
          <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as SalesDocumentStatus | "")}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value="">Svi statusi</option>
            {Object.values(SalesDocumentStatus).map((s) => (
              <option key={s} value={s}>
                {SALES_DOCUMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <div className="w-36">
          <label className="mb-1 block text-xs font-medium text-slate-500">Datum od</label>
          <DateInputDDMMYYYY value={dateFrom} onChange={setDateFrom} />
        </div>
        <div className="w-36">
          <label className="mb-1 block text-xs font-medium text-slate-500">Datum do</label>
          <DateInputDDMMYYYY value={dateTo} onChange={setDateTo} />
        </div>

        {(search || status || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setStatus("");
              setDateFrom("");
              setDateTo("");
            }}
            className="h-10 text-sm text-slate-500 hover:text-slate-700"
          >
            Poništi filtere
          </button>
        )}
        </div>
      )}

      {tab === "DELIVERY_NOTE" ? (
        <div className="rounded-xl border border-border bg-surface shadow-subtle">
          {isLoadingDeliveryNotes ? (
            <div className="space-y-2 p-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !deliveryNotes || deliveryNotes.length === 0 ? (
            <div className="p-5">
              <EmptyState
                icon={Truck}
                title="Nema otpremnica"
                description='Otpremnica se kreira sa strane fakture, dugmetom "Kreiraj otpremnicu".'
              />
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Broj</th>
                  <th>Kupac</th>
                  <th>Datum</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {deliveryNotes.map((note) => (
                  <tr
                    key={note.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/prodaja/${note.salesDocumentId}`)}
                  >
                    <td className="font-medium text-slate-800">{note.documentNumber ?? "—"}</td>
                    <td className="text-slate-600">{note.customerName ?? "—"}</td>
                    <td className="text-slate-500">{new Date(note.issueDate).toLocaleDateString("sr-RS")}</td>
                    <td>
                      <StatusBadge
                        label={note.status === "CONFIRMED" ? "Prijem potvrđen" : "Poslato, čeka potvrdu"}
                        tone={note.status === "CONFIRMED" ? "success" : "warning"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={FileStack}
              title={tab === "QUOTE" ? "Nema ponuda za ovaj filter" : "Nema faktura za ovaj filter"}
              description="Promenite filtere ili kreirajte novi dokument dugmetom iznad."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Broj</th>
                <th>Kupac</th>
                <th>Datum izdavanja</th>
                <th>{tab === "QUOTE" ? "Rok važenja" : "Rok plaćanja"}</th>
                <th>Ukupno</th>
                {tab === "INVOICE" && <th>Preostalo</th>}
                <th>Status</th>
                <th>Tip</th>
              </tr>
            </thead>
            <tbody>
              {data.map((doc) => (
                <tr
                  key={doc.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/prodaja/${doc.id}`)}
                >
                  <td className="font-medium text-slate-800">{doc.documentNumber ?? "Nacrt"}</td>
                  <td className="text-slate-600">{doc.customer?.name ?? "—"}</td>
                  <td className="text-slate-500">{new Date(doc.issueDate).toLocaleDateString("sr-RS")}</td>
                  <td className="text-slate-500">
                    {doc.validUntilOrDueDate
                      ? new Date(doc.validUntilOrDueDate).toLocaleDateString("sr-RS")
                      : "—"}
                  </td>
                  <td>{formatMoney(doc.grossAmount, doc.currency)}</td>
                  {tab === "INVOICE" && <td>{formatMoney(doc.dueAmount, doc.currency)}</td>}
                  <td>
                    <StatusBadge
                      label={SALES_DOCUMENT_STATUS_LABELS[doc.status]}
                      tone={SALES_DOCUMENT_STATUS_TONE[doc.status]}
                    />
                  </td>
                  <td className="text-slate-500">{SALES_DOCUMENT_TYPE_LABELS[doc.type]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}
